import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import type { RoleListItemWithDirectory } from "@/api/roles-permissions/rolesDirectory.types";
import { AddOrEditRoleModal } from "@/components/features/RolesPermissions/AddOrEditRoleModal";
import { Button } from "@/components/ui/Button/Button";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import { DeactivateRoleModal } from "@/components/features/RolesPermissions/DeactivateRoleModal/DeactivateRoleModal";
import { rolesDirectoryClient } from "@/api/roles-permissions/rolesDirectory.client";
import { useBypassOr } from "@/hooks/access/useBypassOr";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useDuplicateRole } from "@/hooks/roles-permissions/useDuplicateRole";
import { useOpenRole } from "@/hooks/roles-permissions/useOpenRole";
import { useActivateDeactivateRole } from "@/hooks/roles-permissions/useRolesPermissionsMutations";
import {
  getAllRoles,
  useRoleDeactivateImpact,
  useRoleHoldersPreview,
} from "@/hooks/roles-permissions/useRolesPermissionsQueries";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { Alert, Table } from "@onengine/component-library";
import { Box, Flex, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus, IconRefresh } from "@tabler/icons-react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { useCallback, useMemo, useState } from "react";
import { useRolesListColumns } from "@/components/features/RolesPermissions/useRolesListColumns";
import styles from "./roles-permissions.module.scss";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { useServerTable } from "@/hooks/table/useServerTable";
import type { TableFilterColumnConfig } from "@/hooks/table/useTableFilterOptions";

export const Route = createFileRoute("/_authenticated/roles-permissions/")({
  component: RolesPermissions,
  validateSearch: tableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const getRoleSortByField = (columnId: string): string | undefined =>
  columnId === "name" ? "name" : undefined;

const ROLES_FILTER_COLUMNS: TableFilterColumnConfig[] = [
  {
    columnId: "name",
    filterByKey: "roleName",
    storeType: "roles",
    searchParams: { type: "roles", fields: ["label"], limit: 10, key: "roleName" },
  },
  {
    columnId: "status",
    filterByKey: "status",
    storeType: "roles",
    // The list query filters status by the option's value string (see fetchAllRoles
    // getValue), so send the label/value rather than a resolved id.
    valueSource: "label",
    searchParams: { type: "status", fields: ["value"], limit: 10, key: "status" },
  },
];

function RolesPermissions() {
  const searchParams = useSearch({ from: "/_authenticated/roles-permissions/" });
  const { hasAccess, isSuperAdmin } = usePermissions();
  useBreadcrumb(
    [
      {
        title: "Roles & Permissions",
        href: "/roles-permissions",
        icon: getRouteIcon(MODULE_KEYS_MAP.rolesPermissions),
      },
    ],
    { listView: "List View" },
  );
  const { edit: hasRoleEdit, create: hasRoleCreate } = hasAccess({
    module: MODULE_KEYS_MAP.rolesPermissions,
  });
  // The role write applies to every company's copy, so the backend takes it from super users only.
  const canEditRole = isSuperAdmin && hasRoleEdit;
  const canCreateRole = isSuperAdmin && hasRoleCreate;

  // Activate and deactivate are taken from a super admin or an Entity Admin of the request's company
  // (SERV-3572), which is the engine's bypass there, not a role name from the sign-in response.
  const canToggleRoleStatus = useBypassOr(isSuperAdmin);

  const [isAddRoleModalOpened, rolesModalHandlers] = useDisclosure(false);
  const [isDeactivateModalOpen, deactivateModalHandlers] = useDisclosure(false);
  const [isActivateModalOpen, activateModalHandlers] = useDisclosure(false);
  const [selectedRole, setSelectedRole] = useState<RoleListItemWithDirectory | null>(null);

  const { queryOptions, isPrefsLoading, controlProps, tableKey } = useServerTable({
    viewName: "roleList",
    columns: ROLES_FILTER_COLUMNS,
    getSortByField: getRoleSortByField,
    enableUrlSync: true,
    searchParams,
    defaultStoreType: "roles",
  });

  const {
    data: rolesData,
    isPending: isRolesLoading,
    isFetching,
    isError: isRolesError,
    refetch: refetchRoles,
  } = getAllRoles({
    page: queryOptions.page.toString(),
    pageSize: queryOptions.pageSize.toString(),
    search: queryOptions.search,
    sortBy: queryOptions.sortBy,
    sortOrder: queryOptions.sortOrder,
    filters: queryOptions.filters,
  });

  const { activateRoleMutation, deactivateRoleMutation, isActivatingRole, isDeactivatingRole } =
    useActivateDeactivateRole();

  const { mutate: activateRole } = activateRoleMutation;
  const { mutate: deactivateRole } = deactivateRoleMutation;

  const { openRole } = useOpenRole();

  // Access is edited on the role record's Access tab; the full-page editor is now the create
  // path only, so the row menu opens the record rather than that page.
  const handleEditAccess = useCallback(
    (row: RoleListItemWithDirectory) => void openRole(row, "access"),
    [openRole],
  );

  const handleDuplicate = useDuplicateRole();

  const navigate = useNavigate();
  const handleCompare = useCallback(
    (row: RoleListItemWithDirectory) =>
      void navigate({ to: "/roles-permissions/compare", search: { role: row.id } }),
    [navigate],
  );

  const handleToggleStatus = useCallback(
    (row: RoleListItemWithDirectory) => {
      setSelectedRole(row);
      if (row.status === "active") {
        deactivateModalHandlers.open();
      } else {
        activateModalHandlers.open();
      }
    },
    [activateModalHandlers, deactivateModalHandlers],
  );

  const columns = useRolesListColumns({
    filterColumns: ROLES_FILTER_COLUMNS,
    canEditRole,
    canCreateRole,
    canToggleRoleStatus,
    isActivatingRole,
    isDeactivatingRole,
    onOpenRole: openRole,
    onEditAccess: handleEditAccess,
    onDuplicate: handleDuplicate,
    onCompare: handleCompare,
    onToggleStatus: handleToggleStatus,
  });
  const hasRoles = (rolesData?.data?.length ?? 0) > 0;
  const hasActiveFilters = queryOptions.filters.length > 0;
  const hasSearchOrFilters = (queryOptions.search ?? "").trim().length > 0 || hasActiveFilters;
  const shouldShowEmptyState = !hasRoles && !hasSearchOrFilters && !isRolesLoading && !isRolesError;
  // A failed load with nothing cached is not an empty list: the Alert says what happened and the
  // table would otherwise claim "No roles found" (frame 4).
  const hasNothingToShow = isRolesError && !hasRoles;

  const handleDeactivateConfirm = () => {
    if (!selectedRole) return;
    deactivateRole(selectedRole.id, {
      onSettled: () => {
        deactivateModalHandlers.close();
        setSelectedRole(null);
      },
    });
  };

  const handleActivateConfirm = () => {
    if (!selectedRole) return;
    activateRole(selectedRole.id, {
      onSettled: () => {
        activateModalHandlers.close();
        setSelectedRole(null);
      },
    });
  };

  const deactivatingRoleId = isDeactivateModalOpen && selectedRole ? selectedRole.id : null;
  const { data: holdersPreview } = useRoleHoldersPreview(deactivatingRoleId);
  const { data: impactRead, isError: impactCheckFailed } =
    useRoleDeactivateImpact(deactivatingRoleId);
  const deactivateImpact = useMemo(
    () =>
      selectedRole
        ? rolesDirectoryClient.getDeactivateImpact(
            selectedRole,
            holdersPreview ?? null,
            impactRead ?? null,
          )
        : { holders: 0, namedHolders: [], losingAllAccess: [] },
    [selectedRole, holdersPreview, impactRead],
  );

  const handleDeactivateClose = () => {
    deactivateModalHandlers.close();
    setSelectedRole(null);
  };

  const handleActivateClose = () => {
    activateModalHandlers.close();
    setSelectedRole(null);
  };

  return (
    <>
      <Flex direction="column" className={styles.container}>
        <Flex justify="space-between" align="center" mb="var(--spacing-medium)">
          <Title order={3} className={styles.title}>
            Roles & Permissions
          </Title>
          {canCreateRole && (
            <Button
              variant="primary"
              onClick={rolesModalHandlers.open}
              iconLeft={<IconPlus size={16} />}
            >
              New Role
            </Button>
          )}
        </Flex>
        {isRolesError && (
          <Box mb="var(--spacing-large)">
            <Alert
              type="danger"
              variant="strong"
              size="base"
              closable={false}
              title="Could not load roles"
              message="Check your connection and try again."
              actions={[
                {
                  label: "Retry",
                  onClick: () => refetchRoles(),
                  iconLeft: <IconRefresh size={16} />,
                },
              ]}
            />
          </Box>
        )}
        {hasNothingToShow ? null : shouldShowEmptyState ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="var(--spacing-x-large)"
            className={styles.emptyStateContainer}
          >
            <Flex direction="column" align="center" gap="var(--spacing-x-small)">
              <Text className={styles.heading}>No roles have been created</Text>
              {canCreateRole && (
                <Text className={styles.description}>
                  You can create a new role by clicking on
                  <Text component="span"> &quot;New Role&quot;</Text> below
                </Text>
              )}
            </Flex>
            {canCreateRole && (
              <Box>
                <Button
                  variant="primary"
                  onClick={rolesModalHandlers.open}
                  iconLeft={<IconPlus size={16} />}
                  size="large"
                >
                  New Role
                </Button>
              </Box>
            )}
          </Flex>
        ) : (
          <Flex direction="column" gap="var(--spacing-large)">
            <Table<RoleListItemWithDirectory>
              key={tableKey}
              {...controlProps}
              cellClickIgnoreColumns={["more"]}
              onRowClick={openRole}
              columns={columns}
              data={rolesData?.data ?? []}
              loading={isFetching || isPrefsLoading || isRolesLoading}
              totalCount={rolesData?.total ?? 0}
              // A page that is just a table stops 24px above the bottom and scrolls its rows
              // with the header pinned, rather than scrolling the page (Brain team-assignments
              // §17; design-changes 2026-10-24). `fillHeight` is the library's own 24px gap.
              fillHeight
              emptyText="No roles found"
            />
          </Flex>
        )}
      </Flex>

      <AddOrEditRoleModal
        isOpen={isAddRoleModalOpened}
        onClose={rolesModalHandlers.close}
        type="add"
      />

      <DeactivateRoleModal
        isOpen={isDeactivateModalOpen}
        roleName={selectedRole?.name ?? "this role"}
        impact={deactivateImpact}
        impactCheckFailed={impactCheckFailed}
        isDeactivating={isDeactivatingRole}
        onClose={handleDeactivateClose}
        onConfirm={handleDeactivateConfirm}
      />

      <ConfirmationModal
        isOpen={isActivateModalOpen}
        onClose={handleActivateClose}
        title="Activate role?"
        body="This role will become active and available for assignment again, with its existing permissions restored as configured. Users previously assigned will not be automatically re-added, you'll need to assign them again."
        confirmText="Activate"
        confirmVariant="primary"
        isConfirmLoading={isActivatingRole}
        onConfirm={handleActivateConfirm}
      />
    </>
  );
}
